import '@fontsource/inter/400.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/700.css';
import jsPDF from 'jspdf';
import { domToWebp } from 'modern-screenshot';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Case, Switch } from 'react-if';
import { useParams } from 'react-router-dom';
import GridLoader from 'react-spinners/GridLoader';
import { getServiceUrl } from '../../api-v2/apis-request';
import '../../styles/tailwind.css';
import appstore from './assets/images/appstore.png';
import brandLogoPlaceholder from './assets/images/brand.png';
import discountLogo from './assets/images/discount.png';
import freeproductLogo from './assets/images/freeproduct.png';
import giftcardLogo from './assets/images/giftcard.png';
import endvr_logo from './assets/images/logo.svg';
import moneyLogo from './assets/images/money.png';
import pipe from './assets/images/pipe.svg';
import playstore from './assets/images/playstore.png';
import prizeLogo from './assets/images/prize.png';
import './BackgroundImage.css';

import { waitUntilAllSupportedFontsLoad } from '../../utils/font';

const PosterSize = { width: 1063, height: 1380 };

const isDebug = process.env.REACT_APP_DEBUG === 'true';

const CampaignPoster = () => {
	const contentRef = useRef();
	const { bundleId, language = 'en' } = useParams();
	const lowerCaseLanguage = language.toLowerCase();
	const [status, setStatus] = useState(/** @type {'loading' | 'success' | 'error'} */ ('loading'));
	const [result, setResult] = useState({});

	const [height, setHeight] = useState(80);

	const [campaignImageBase64, setCampaignImageBase64] = useState(
		/** @type {string | null} */ (null)
	);
	const [brandLogoBase64, setBrandLogoBase64] = useState(/** @type {string | null} */ (null));

	const [brandLogo, setBrandLogo] = useState(/** @type {string | null} */ (null));

	const [generatedPdf, setGeneratedPdf] = useState(/** @type {jsPDF | null} */ (null));

	const [imagesLoaded, setImagesLoaded] = useState({
		campaignImage: false,
		brandLogo: false,
	});

	const generatePdf = useCallback(async () => {
		await waitUntilAllSupportedFontsLoad();

		const imgData = await domToWebp(contentRef.current, {
			scale: 2, // Increase scale for better quality
			backgroundColor: '#FBFAFC', // Background is black by default
			width: PosterSize.width,
			height: PosterSize.height,
		});

		// Initialize jsPDF
		const pdf = new jsPDF({
			orientation: 'portrait',
			unit: 'px',
			format: [PosterSize.width, PosterSize.height],
		});

		pdf.addImage(imgData, 'JPEG', 0, 0, PosterSize.width, PosterSize.height);
		setGeneratedPdf(pdf);

		pdf.save(`poster_${bundleId}.pdf`);
	}, [bundleId]);

	const retryDownloadPdf = useCallback(() => {
		if (!generatedPdf) return;
		generatedPdf.save(`poster_${bundleId}.pdf`);
	}, [bundleId, generatedPdf]);

	useEffect(() => {
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
		getBundlePosterData(userTimeZone);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getBundlePosterData = async (userTimeZone) => {
		const baseUrl = getServiceUrl('campaign/read');

		let responseJson;
		try {
			const response = await fetch(
				`${baseUrl}/external/poster/details/${bundleId}/${lowerCaseLanguage}?timezone=${userTimeZone}`,
				{
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
				}
			);
			responseJson = await response.json();
		} catch (error) {
			console.log('Error fetching bundle data:', error);
		}

		if (!responseJson?.status) {
			setStatus('error');
			return;
		}

		const { result } = responseJson;

		if (result.rewards?.length > 4) {
			const newHeight = Math.min(420 / result.rewards.length - 20, 80);
			setHeight(Math.min(newHeight, 80));
		}

		setResult(result);

		if (result.bundle?.imageUrl) {
			try {
				setCampaignImageBase64(await imageUrlToBase64(result.bundle?.imageUrl));
			} catch (error) {}
		}

		/* Use empty string as default value so the `img`'s `onError` callback is
		called. When not providing a `src` attribute, there will be no image to load
		and, therefore, no error */
		setBrandLogo(result.brand?.logoUrl ?? '');

		if (result.brand?.logoUrl) {
			try {
				setBrandLogoBase64(await imageUrlToBase64(result.brand?.logoUrl));
			} catch (error) {}
		}

		setStatus('success');
	};

	const allImagesLoaded = useMemo(
		() => Object.values(imagesLoaded).every((value) => value),
		[imagesLoaded]
	);

	// Generate the PDF when all images are loaded
	useEffect(() => {
		if (allImagesLoaded && !generatedPdf) {
			generatePdf();
		}
	}, [allImagesLoaded, generatePdf, generatedPdf]);

	return (
		<Switch>
			<Case condition={status === 'success'}>
				{/* Only render the poster if the PDF wasn't generated yet */}
				{(isDebug || !generatedPdf) && (
				<div
					ref={contentRef}
					className="mb-[1200px] w-[1063px] px-10 py-5"
					style={{ background: '#FBFAFC' }}
				>
					<div className="relative flex items-center justify-between">
						<div className="flex items-center overflow-hidden">
							<img
								src={brandLogoBase64 || brandLogo}
								alt={`${result.brand?.name} Logo`}
								className="w-[48px] h-full object-contain rounded-[15px]"
								onLoad={() => {
									setImagesLoaded((prev) => ({ ...prev, brandLogo: true }));
								}}
								onError={() => {
									// Use placeholder image if brand logo failed to load
									setBrandLogo(brandLogoPlaceholder);
								}}
							/>

							<div className="text-[#343640] text-[40px] font-bold font-poppins break-words sans-serif ml-[24px] line-clamp-1 text-ellipsis">
								{result.brand?.name}
							</div>
						</div>
						<div className="flex items-center shrink-0">
							<div className="ml-[24px]">
								<img src={pipe} className="h-full" alt="" />
							</div>
							<div className="ml-[24px] h-[31px]">
								<img src={endvr_logo} className="h-full" alt="" />
							</div>
						</div>
					</div>
					<div className="grid grid-rows-2 items-center justify-left">
						<h1 className="bundle-name-top-text">{result.bundle?.name || 'No name available'}</h1>
					</div>
					<div className="background-container relative">
						<img
							src={campaignImageBase64 || result.bundle?.imageUrl}
							alt=""
							className="background-image"
							onLoad={() => {
								setImagesLoaded((prev) => ({ ...prev, campaignImage: true }));
							}}
							onError={() => {
								// Ignore error if campaign image failed to load
								setImagesLoaded((prev) => ({ ...prev, campaignImage: true }));
							}}
						/>

						<div className="text-container absolute z-20 top-0 bg-gradient-to-l from-white/40 via-white/65 to-white/95">
							<div className="text-container">
								{result.rewards?.map((reward, index) => (
									<div key={index} className="relative mt-[24px]" style={{ height: height }}>
										<div className="relative flex items-center justify-left gap-3">
											<Switch>
												<Case condition={reward.TYPE === 'prize'}>
													<img
														src={prizeLogo}
														alt={`${result.brand?.name} Logo`}
														className="w-[48px] h-full object-contain rounded-[15px]"
														style={{ height: `${height}px` }}
													/>
													<div
														className="text-[#343640] font-poppins overflow-hidden text-ellipsis whitespace-nowrap font-bold"
														style={{
															fontSize: `24px`,
															width: '100%',
														}}
													>
														<p>{reward.TITLE}</p>
													</div>
												</Case>
												<Case condition={reward.TYPE === 'free_product'}>
													<img
														src={freeproductLogo}
														alt={`${result.brand?.name} Logo`}
														className="w-[48px] h-full object-contain rounded-[15px]"
														style={{ height: `${height}px` }}
													/>
													<div
														className="text-[#343640] font-poppins overflow-hidden text-ellipsis whitespace-nowrap font-bold"
														style={{
															fontSize: `24px`,
															width: '100%',
														}}
													>
														<p>{reward.TITLE}</p>
													</div>
												</Case>
												<Case condition={reward.TYPE === 'discount'}>
													<img
														src={discountLogo}
														alt={`${result.brand?.name} Logo`}
														className="w-[48px] h-full object-contain rounded-[15px]"
														style={{ height: `${height}px` }}
													/>
													<div
														className="text-[#343640] font-poppins overflow-hidden text-ellipsis whitespace-nowrap font-bold"
														style={{
															fontSize: `24px`,
															width: '100%',
														}}
													>
														<p>{reward.TITLE}</p>
													</div>
												</Case>
												<Case condition={reward.TYPE === 'gift_card'}>
													<img
														src={giftcardLogo}
														alt={`${result.brand?.name} Logo`}
														className="w-[48px] h-full object-contain rounded-[15px]"
														style={{ height: `${height}px` }}
													/>
													<div
														className="text-[#343640] font-poppins overflow-hidden text-ellipsis whitespace-nowrap font-bold"
														style={{
															fontSize: `24px`,
															width: '100%',
														}}
													>
														<p>{reward.TITLE}</p>
													</div>
												</Case>
												<Case condition={reward.TYPE === 'money'}>
													<img
														src={moneyLogo}
														alt={`${result.brand?.name} Logo`}
														className="w-[48px] h-full object-contain rounded-[15px]"
														style={{ height: `${height}px` }}
													/>
													<div
														className="text-[#343640] font-poppins overflow-hidden text-ellipsis whitespace-nowrap font-bold"
														style={{
															fontSize: `24px`,
															width: '100%',
														}}
													>
														<p>
															{reward.TITLE}&nbsp;{lowerCaseLanguage === 'fr' ? 'Comptant' : 'Cash'}
														</p>
													</div>
												</Case>
											</Switch>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="h-[430px] w-[983px]" style={{ background: '#FBFAFC' }}>
						<div className="py-5 items-center gap-20">
							<div>
								<div className="bottom-0 p-2 text-left">
									<div className="text-[#343640] text-[24px] leading-[32px] break-words font-inter font-normal sans-serif h-full">
										{lowerCaseLanguage === 'fr' ? (
											<span className="text-[24px]">
												{result.bundle?.endDate === '' ? (
													<>
														&nbsp;Rejoignez notre campagne et commencez-la le{' '}
														{result.bundle?.launchDate} pour gagner des récompenses exclusives! La
														campagne se poursuivra jusqu'à ce que toutes les récompenses soient
														réclamées.
													</>
												) : (
													<>
														Rejoignez notre campagne et terminez-la entre le{' '}
														{result.bundle?.launchDate} et le {result.bundle?.endDate} pour gagner
														des récompenses exclusives!
													</>
												)}
											</span>
										) : (
											<>
												<span className="text-[24px]">
													{result.bundle?.endDate === '' ? (
														<>
															&nbsp;Join our campaign and complete it starting on{' '}
															{result.bundle?.launchDate} to earn exclusive rewards! The campaign
															will run until all rewards are claimed.
														</>
													) : (
														<>
															{' '}
															Join our campaign and complete it between {
																result.bundle?.launchDate
															}{' '}
															and {result.bundle?.endDate ?? ''} to earn exclusive rewards!
														</>
													)}
												</span>
											</>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="flex justify-end items-center gap-20">
							<div className="flex flex-col justify-between w-4/5">
								<div className="text-left w-full">
									<div className="w-full text-[#6A00F4] text-[32px] font-poppins font-bold leading-[40px] break-words">
										{lowerCaseLanguage === 'fr' ? 'COMMENCER EST FACILE' : 'GETTING STARTED IS EASY'}
									</div>
									<div className="text-[#343640] text-[24px] font-inter font-weight: 400 mt-6 h-[160px]">
										{lowerCaseLanguage === 'fr' ? (
											<ul className="list-none space-y-3">
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">1</span>
													<p className="text-[18px] font-bold">
														Scannez le code QR ou téléchargez ENDVR depuis l'App Store ou le Play
														Store
													</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">2</span>
													<p className="text-[18px] font-bold">Rejoignez votre magasin</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">3</span>
													<p className="text-[18px] font-bold">
														Gagnez de l'argent instantanément, des réductions, des produits et des
														prix des marques que vous vendez en magasin
													</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">4</span>
													<p className="text-[18px] font-bold">
														Parrainez vos collègues pour gagner encore plus
													</p>
												</li>
											</ul>
										) : (
											<ul className="list-none space-y-3">
												<li className="flex items-center">
													<span className="font-bold flex items-center mr-4">1</span>
													<p className="text-[18px] font-bold">
														Scan QR Code or Download ENDVR from Apple or Google Play Store
													</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4 text-center">2</span>
													<p className="text-[18px] font-bold">Join Your Store</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">3</span>
													<p className="text-[18px] font-bold">
														Earn instant cash, discounts, product and prizes from the brands you
														sell in store
													</p>
												</li>
												<li className="flex items-center">
													<span className="font-bold text-[24px] mr-4">4</span>
													<p className="text-[18px] font-bold">
														Refer your colleagues to earn even more
													</p>
												</li>
											</ul>
										)}
									</div>
								</div>
								<div className="flex w-full mt-3">
									<img src={appstore} alt="Download from App Store" />
									<img src={playstore} alt="Download from Play Store" />
								</div>
							</div>

							<div className="flex flex-col w-1/3">
								{result.bundle?.deeplink && (
									<div
										className="w-[264px] h-[192px] bg-white rounded-[15px] p-3"
										style={{ border: '1px solid #E1E1E1' }}
									>
										<div className="w-full h-full flex items-center justify-center">
											<div
												className="w-[160px] h-[160px] bg-white shadow-md rounded-lg border border-gray-200 p-2"
												style={{ filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))' }}
											>
												<QRCodeSVG
													value={result.bundle.deeplink}
													style={{ width: 144, height: 144 }}
												/>
											</div>
										</div>
									</div>
								)}
								<div
									className="flex h-[80px] w-[264px]"
									style={{
										border: '1px solid #E1E1E1',
										borderRadius: '15px',
										padding: '12px',
										background: 'white',
										margin: '16px 0px 0px 0px ',
									}}
								>
									<div
										style={{
											color: '#777777',
											fontSize: '16px',
											fontFamily: 'Inter',
											fontWeight: '600',
											lineHeight: '24px',
											wordWrap: 'break-word',
										}}
									>
										{lowerCaseLanguage === 'en' ? 'Referral Code:' : 'Code de référence:'}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

				{!generatedPdf && <Loading />}

				{!isDebug && generatedPdf !== null && (
					<div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center gap-8">
						{lowerCaseLanguage === 'fr' ? (
							<>
								<h1 className="text-3xl">Votre affiche de campagne est prête !</h1>
								<p className="max-w-md text-balance text-center">
									Si votre affiche ne s'est pas téléchargée automatiquement,{' '}
									<button className="text-blue-500 underline" onClick={retryDownloadPdf}>
										cliquez ici
									</button>{' '}
									pour la télécharger maintenant.
								</p>
							</>
						) : (
							<>
								<h1 className="text-3xl">Your campaign poster is ready!</h1>
								<p className="max-w-md text-balance text-center">
									If your poster didn't download automatically,{' '}
									<button className="text-blue-500 underline" onClick={retryDownloadPdf}>
										click here
									</button>{' '}
									to download it now.
								</p>
							</>
						)}
					</div>
				)}
			</Case>
			<Case condition={status === 'loading'}>
				<Loading />
			</Case>
			<Case condition={status === 'error'}>
				<div className="flex w-screen h-screen flex-col gap-[32px] items-center flex-nowrap justify-center">
					<div className="w-[180px] h-[180px] shrink-0 bg-[url(img/cashout/error-icon.png)] bg-cover bg-no-repeat relative overflow-hidden" />
					<div className="flex w-full flex-col gap-[16px] justify-center items-center shrink-0 flex-nowrap relative">
						<span className="text-2xl flex w-[948px] h-[56px] justify-center items-start shrink-0 basis-auto font-['Roboto'] md:text-[48px] font-bold leading-[56px] text-[#000] relative text-center whitespace-nowrap">
							Invalid Request!
						</span>
						<div className="flex w-full px-2 text-center flex-col items-start shrink-0 flex-nowrap relative">
							<span className="flex w-full h-[24px] justify-center items-start shrink-0 basis-auto font-['Roboto'] text-sm md:text-[18px] font-medium leading-[24px] text-[#000] relative text-center whitespace-nowrap">
								This request is invalid, please check your email received
							</span>
						</div>
					</div>
				</div>
			</Case>
		</Switch>
	);
};

const Loading = () => {
	return (
		<div className="fixed inset-0 flex justify-center items-center z-50 bg-white">
			<GridLoader size={24} color="rgb(30 14 48)" />
		</div>
	);
};

/**
 * @param {Blob} blob
 * @returns {Promise<string | ArrayBuffer | null>}
 */
function imageBlobToBase64(blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => resolve(reader.result);
		reader.onerror = (event) => reject(event);

		reader.readAsDataURL(blob);
	});
}

/**
 * @param {string} url
 * @returns {Promise<string | ArrayBuffer | null>}
 */
async function imageUrlToBase64(url) {
	// The search param is used to avoid CORS issues with AWS S3 and Digital Ocean CDN
	const urlObject = new URL(url);
	urlObject.searchParams.set('v', Date.now());
	url = urlObject.toString();

	const response = await fetch(url);
	const blob = await response.blob();
	return await imageBlobToBase64(blob);
}

export default CampaignPoster;
